import { z } from 'zod';
/** Every manifest Civil writes carries this. Bump only with a migration path. */
export const CIVIL_API_VERSION = 'civil/v1';
/**
 * PRD 6.4 states `^[a-z][a-z0-9-]{0,63}$`, but its own example graph declares
 * `search_tools`. Widened to admit underscores so a code node's id can match the
 * Python module it points at. See docs/prd-deltas.md.
 */
export const ID_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/;
export const zId = z
    .string()
    .regex(ID_PATTERN, 'must be lowercase letters, digits, hyphens and underscores, starting with a letter, max 64 chars');
export const zApiVersion = z.literal(CIVIL_API_VERSION);
export const zMetadata = z.object({
    id: zId,
    name: z.string().min(1).optional(),
});
/** A path relative to the project root. Resolution is checked separately (see validate.ts). */
export const zRelPath = z.string().min(1).refine((p) => !p.startsWith('/') && !p.includes('..'), {
    message: 'must be a project-relative path without ".." segments',
});
export const zPoint = z.object({ x: z.number(), y: z.number() });
/**
 * A schedule, as cron says it: five fields (six with seconds, seven with a year), each
 * a list of values, ranges, steps, names (`MON`, `JAN`), or `*`, `?`, `L`, `W`, `#` —
 * or one of cron's `@` names. `@unknown` is Civil's own: a process the code runs on a
 * schedule nobody wrote down where it could be read.
 *
 * Schedules arrive from code Civil reads and from a model's reading of it, so a value
 * outside this grammar is refused, not kept: `0 {start_hour|stop_hour} * * 1-5` is a
 * template, not a schedule, and kept as text it broke the document it was written into.
 */
const CRON_NAMES = /^@(yearly|annually|monthly|weekly|daily|midnight|hourly|unknown)$/;
const CRON_ITEM = /^(?:\*|\?|L|\d{1,4}[LW]?|[A-Za-z]{3}L?)(?:-(?:\d{1,4}|[A-Za-z]{3}))?(?:\/\d{1,4})?(?:#[1-5])?$/;
export function isCron(value) {
    if (CRON_NAMES.test(value))
        return true;
    if (value.length > 120 || value !== value.trim())
        return false;
    const fields = value.split(/ +/);
    return fields.length >= 5 && fields.length <= 7 && fields.every((f) => f.split(',').every((item) => CRON_ITEM.test(item)));
}
export const zCron = z.string().refine(isCron, {
    message: 'must be a cron expression (five fields, e.g. "0 3 * * *") or one of @hourly, @daily, @weekly, @monthly, @yearly, @unknown',
});
/**
 * PRD 6.3: layout is a SIBLING of spec, never inside it. Dragging a node must not
 * produce a diff that looks like a semantic change.
 */
export const zLayout = z
    .object({ nodes: z.record(zId, zPoint).default({}) })
    .default({ nodes: {} });
//# sourceMappingURL=common.js.map